import { GridParentFilter } from '../../../../models/ts/grid-parent-filter.model';
import { createActionGroup, emptyProps, props } from '@ngrx/store';
import { StoreCollectionForm, StoreCollectionFormInitializationStatus } from './forms-state';
import { Update } from '@ngrx/entity';
import { LinkedCollectionType } from '../../../../models/ts/linked-collection-type.model';
import { CollectionForm } from '../../../../models/ts/collection-form.model';
import { CollectionFormField } from '../../../../models/ts/collection-form-field.model';
import { ViewDataSource } from '../../../../models/ts/view-data-source.model';
import { HttpErrorResponse } from '@angular/common/http';
import { CollectionFormValidatorResult } from '../../../features/bizzmine/form/classes/collection-form-validator-result';
import { CreatedFormInstance } from '../../../features/bizzmine/form/classes/created-form-instance';
import { SchedulerDto } from 'src/models/ts/scheduler-dto.model';
import { OrganizationChartItemType } from '../../../../models/ts/organization-chart-item-type.model';
import { Record } from 'src/models/ts/collection-form-field.model';
import { StateType } from '../../../../models/ts/state-type.model';
import { CollectionMethodType } from '../../../../models/ts/collection-method-type.model';
import { CollectionFormFieldGridLookupData } from '../../../../models/ts/collection-form-field-grid-lookup-data.model';
import { CollectionFormFieldSingleLookupData } from '../../../../models/ts/collection-form-field-single-lookup-data.model';
import { VersionType } from 'src/app/shared/enums/version-type';
import { MediaCommitDto } from '../../../../models/ts/media-commit-dto.model';
import { FlowStatusTaskDto } from '../../../../models/ts/flow-status-task-dto.model';
import { CollectionListDataInstance } from '../../../shared/interfaces/collection-list-data-instance';
import { ReadOnlyPriority } from '../../../features/bizzmine/form/enums/read-only-priority.enum';
import { CollectionFormExternalAccessDto } from '../../../../models/ts/collection-form-external-access-dto';
import { SkillIntegrityCheck } from '../../../../models/ts/skill-integrity-check.model';
import { CollectionListSummaryItem, LookupItem } from '../../../../models/ts/collection-list-summary-item.model';
import { OrgChartItem } from '../../../features/bizzmine/org-chart/interfaces/org-chart-item';
import { TrnFieldProperties } from '../../../../models/ts/trn-field-properties-dto.model';
import { CollectionFormFormulaDto } from '../../../../models/ts/collection-form-formulas-tag-dto.model';
import { InstancePermissionsDto } from 'src/models/ts/instance-permissions-dto.model';
import { AiButtonModel } from '../../../../models/ts/ai-button-model';
import { CollectionFormFieldValue } from '../../../../models/ts/collection-form-field-value.model';
import { EvaluationActionDto } from '../../../../models/ts/evaluation-action-dto.model';
import { ProtectedFieldType } from '../../../../models/ts/protected-field-type.model';
import { AiButtonResponse, AiButtonResultValue } from 'src/app/store/features/forms/effects/forms-ai-effects';
import { TrainingAssessmentDto } from 'src/models/ts/training-assessment-dto.model';

export type SuccessProps = {
  formId: string,
  versionId: number,
  instanceId: number,
  collectionId: number,
  close: boolean,
  guidChecksum?: string | undefined
}

// TODO: GL add more actions and define props
export const formsActions = createActionGroup({
  source: 'Forms',
  events: {
    // Update
    updateForm: props<{ update: Update<StoreCollectionForm> }>(),
    updateTrainingAssessment: props<{ formId: string, trainingAssessment: TrainingAssessmentDto }>(),
    updateFormData: props<{ formId: string, data: CollectionForm }>(),
    refreshForm: props<{
      formId: string,
      collectionId: number,
      instanceId: number,
      versionId: number,
      guidChecksum: string | undefined
    }>(),
    formRefreshed: props<{ formId: string, form: CollectionForm }>(),
    formGroupReset: props<{ formId: string }>(),

    formFinishedLoading: props<{ formId: string }>(),
    formInitializationStatusChanged: props<{ formId: string, status: StoreCollectionFormInitializationStatus }>(),

    hideRow: props<{ formId: string, accordionId: number, rowId: number, hide: boolean }>(),
    hideAccordion: props<{ formId: string, accordionId: number, hide: boolean }>(),

    // Linked Instance
    saveLinkedFormInstance: props<{
      parentFormId: string,
      parentFormFieldId: number,
      formId: string,
      form: CollectionForm,
      closeStep: boolean,
      preValidate: boolean
    }>(),
    saveLinkedFormInstanceSucceeded: props<{
      parentFormId: string,
      parentFormFieldId: number,
      formId: string,
      instance: CreatedFormInstance
    }>(),
    saveLinkedFormInstanceFailed: props<{
      formId: string,
      response: HttpErrorResponse
    }>(),
    saveGridLinkedFormInstance: props<{
      parentFormId: string,
      parentFormFieldId: number,
      parentGridFieldId: number,
      parentRecordId: number,
      formId: string,
      form: CollectionForm,
      closeStep: boolean,
      preValidate: boolean
    }>(),
    saveGridLinkedFormInstanceSucceeded: props<SuccessProps>(),
    createLinkedFormInstance: props<{
      parentFormId: string,
      parentFormFieldId: number,
      formId: string,
      form: CollectionForm,
      closeStep: boolean,
      preValidate: boolean
    }>(),
    createLinkedFormInstanceSucceeded: props<{
      parentFormId: string,
      parentFormFieldId: number,
      formId: string,
      instance: CreatedFormInstance
    }>(),
    createGridLinkedFormInstance: props<{
      parentFormId: string,
      parentFormFieldId: number | undefined,
      parentGridFieldId: number,
      parentRecordId: number | undefined,
      formId: string,
      form: CollectionForm,
      closeStep: boolean,
      preValidate: boolean
    }>(),
    createGridLinkedFormInstanceSucceeded: props<{
      parentFormId: string,
      parentFormFieldId: number | undefined,
      parentGridFieldId: number,
      parentRecordId: number | undefined,
      formId: string,
      instance: CreatedFormInstance
    }>(),
    createLinkedFormInstanceFailed: props<{
      formId: string,
      response: HttpErrorResponse
    }>(),

    // Save
    saveFormInstance: props<{
      formId: string,
      closeStep: boolean,
      closeForm: boolean,
      preValidate: boolean
    }>(),
    saveRevision: props<{
      formId: string,
      closeStep: boolean,
      closeForm: boolean,
      preValidate: boolean
    }>(),
    saveFormScheduler: props<{ formId: string, scheduler: SchedulerDto }>(),
    saveFormInstanceFailed: props<{ formId: string, response: HttpErrorResponse }>(),
    saveFormInstanceSucceeded: props<SuccessProps>(),

    // Create
    createFormInstance: props<{
      formId: string,
      form: CollectionForm,
      closeStep: boolean,
      closeForm: boolean,
      preValidate: boolean
    }>(),
    createDocumentInstance: props<{
      formId: string,
      form: CollectionForm,
      closeStep: boolean,
      closeForm: boolean,
      preValidate: boolean
    }>(),
    createFormInstanceFailed: props<{ formId: string, response: HttpErrorResponse }>(),
    createFormInstanceSucceeded: props<SuccessProps>(),

    // Task
    saveTask: props<{ formId: string, closeForm: boolean, preValidate: boolean }>(),
    saveTaskFailed: props<{ formId: string, response: HttpErrorResponse }>(),
    executeTask: props<{ formId: string, closeForm: boolean, preValidate: boolean }>(),
    executeTaskFailed: props<{ formId: string, response: HttpErrorResponse }>(),
    removeFormLoadingState: props<{ formId: string }>(),
    displayFormlessException: props<{ error: HttpErrorResponse }>(),
    executeTaskSucceeded: props<SuccessProps>(),
    getPendingSteps: props<{
      collectionId: number,
      instanceId: number,
      versionId: number,
      draftId: number | undefined,
      retryCount?: number | undefined,
      formId?: string | undefined,
    }>(),
    executePendingSteps: props<{
      pendingSteps: FlowStatusTaskDto[],
      collectionId: number,
      formId?: string | undefined,
      executorSelected?: boolean | undefined
    }>(),
    openTaskInstanceDialogComponent: props<{
      pendingSteps: FlowStatusTaskDto[],
      collectionId: number,
      formId: string
    }>(),
    // Read => Edit
    editReadOnlyForm: props<{
      formId: string, collectionId: number,
      instanceId: number,
      versionId: number,
      guidChecksum: string | undefined
    }>(),
    editReadOnlyFormFetched: props<{
      formId: string,
      form: CollectionForm
    }>(),
    unAuthorizedFetch: props<{ formId?: string }>(),
    failedFetch: props<{ formId?: string }>(),
    // GET
    getFormByFolder: props<{
      collectionId: number,
      folderId: number,
    }>(),
    getFormInstanceById: props<{
      collectionId: number,
      instanceId: number,
      versionId: number,
      guidChecksum: string | undefined,
      read: boolean,
      schedulerData?: SchedulerDto | undefined,
      versionType?: VersionType,
      draftId?: number,
      checkForWorkFlow?: boolean
      parentData?: {
        parentFormId: string,
        viewDataSourceId: number,
        crossLinkedInstancesId: number,
        formFieldId: number,
        gridFieldId: number,
        relationType: LinkedCollectionType,
      }
    }>(),
    getFormInstanceForGrid: props<{
      parentFormId: string,
      viewDataSourceId: number,
      crossLinkedInstancesId: number,
      formFieldId: number,
      gridFieldId: number,
      relationType: LinkedCollectionType
      read: boolean
    }>(),
    getFormReadOnly: props<{
      collectionId: number,
      instanceId: number,
      versionId: number,
      guidChecksum: string | undefined,
      state?: StateType | undefined,
    }>(),
    getFormCreate: props<{
      collectionId: number,
      crossLinkCollectionId: number | undefined,
      childInstanceId: number | undefined,
      childVersionId: number | undefined,
      originalChildInstanceId: number | undefined,
      widgetId: number | undefined
    }>(),
    getFormEdit: props<{
      collectionId: number,
      instanceId: number,
      versionId: number,
      guidChecksum: string | undefined,
    }>(),
    getFormWorkspace: props<{
      collectionId: number,
      workspaceItemId: number
    }>(),
    getFormByCollectionMethodType: props<{
      collectionId: number,
      instanceId: number,
      versionId: number,
      collectionMethodType: CollectionMethodType
    }>(),
    getTaskForm: props<{ taskId: number, formId?: string | undefined }>(),
    getAnonymousForm: props<{ collectionId: number, formId?: string | undefined }>(),
    getTaskOnBehalfOf: props<{
      taskId: number,
      collectionsId: number,
      assignedToId: number,
      assignedToType: OrganizationChartItemType,
      formId?: string | undefined
    }>(),
    getFormByWidgetId: props<{ collectionId: number, widgetId: number }>(),
    getFormByWidgetIdWithCategory: props<{
      collectionId: number,
      widgetId: number,
      lookupItem: LookupItem,
      skillCategoryCollectionId: number
    }>(),
    getFormByCollectionId: props<{
      collectionId: number,
      formGridParentFilter?: GridParentFilter | undefined,
      addToViewStack: boolean
    }>(),
    getLinkedForm: props<{ formFieldId: number, formId: string, relationType: LinkedCollectionType }>(),
    getExternalLinkedForm: props<{
      externalAccess: CollectionFormExternalAccessDto,
      formFieldId: number,
      formId: string,
      relationType: LinkedCollectionType
    }>(),
    getGridLinkedForm: props<{
      formId: string,
      collectionFieldId: number | undefined,
      gridFieldId: number,
      recordId: number | undefined,
      relationType: LinkedCollectionType
    }>(),
    getIntegrity: props<{
      formId: string
    }>(),
    fetchIntegrity: props<{
      form: StoreCollectionForm
    }>(),
    mapIntegrity: props<{
      form: StoreCollectionForm,
      documentVersionId: number,
      skillVersionId: number,
      examVersionId: number,
      trainingVersionId: number
    }>(),
    getExternalGridLinkedForm: props<{
      externalAccess: CollectionFormExternalAccessDto,
      formId: string,
      collectionFieldId: number | undefined,
      gridFieldId: number,
      recordId: number | undefined,
      relationType: LinkedCollectionType
    }>(),
    getGridLinkedFormWithProtectedFieldValue: props<{
      formId: string,
      collectionFieldId: number | undefined,
      gridFieldId: number,
      recordId: number | undefined,
      relationType: LinkedCollectionType,
      externalAccess: CollectionFormExternalAccessDto | undefined,
      formFields: Array<{ protectedFieldType: ProtectedFieldType, value: any }>
    }>(),
    getGridLinkedFormWithFile: props<{
      formFieldId: number,
      formId: string,
      gridFieldId: number,
      recordId: number,
      externalAccess: CollectionFormExternalAccessDto | undefined,
      relationType: LinkedCollectionType,
      files: Array<File>
    }>(),
    getLinkedFormWithFile: props<{
      formFieldId: number,
      formId: string,
      externalAccess: CollectionFormExternalAccessDto | undefined,
      relationType: LinkedCollectionType,
      files: Array<File>
    }>(),
    getLinkedFormWithTemplate: props<{
      formFieldId: number,
      formId: string,
      externalAccess: CollectionFormExternalAccessDto | undefined,
      relationType: LinkedCollectionType,
      selectedTemplate: {
        mediaId: number,
        sourceCollectionId: number,
        instanceId: number,
        versionId: number,
        targetCollection: number
      }
    }>(),
    getGridLinkedFormWithTemplate: props<{
      formFieldId: number,
      formId: string,
      gridFieldId: number,
      recordId: number,
      externalAccess: CollectionFormExternalAccessDto | undefined,
      relationType: LinkedCollectionType,
      selectedTemplate: {
        mediaId: number,
        sourceCollectionId: number,
        instanceId: number,
        versionId: number,
        targetCollection: number
      }
    }>(),
    getFormByCollectionGridField: props<{ formFieldId: number, addToViewStack: boolean }>(),
    getFormByCollectionField: props<{ formFieldId: number, addToViewStack: boolean }>(),
    getLinkedInstance: props<{
      collectionId: number,
      instanceId: number,
      versionId: number,
      read: boolean,
      formFieldId: number,
      formId: string,
      relationType: LinkedCollectionType,
      guidChecksum: string | undefined,
    }>(),
    getGridLinkedInstance: props<{
      collectionId: number,
      instanceId: number,
      versionId: number,
      read: boolean,
      formFieldId: number,
      formId: string,
      gridFieldId: number,
      recordId: number,
      relationType: LinkedCollectionType,
      guidChecksum: string | undefined
    }>(),
    getSchedulerByWidgetId: props<{ widgetId: number }>(),

    // Get Form Failure
    getFormFailed: props<{ formId: string | undefined, response: HttpErrorResponse }>(),
    redirectToExam: props<{ taskId: number }>(),
    // GET API Responses
    formFetched: props<{ form: StoreCollectionForm, addToViewStack: boolean }>(),
    updateTrainingDocumentFields: props<{ formId: string }>(),
    fetchFromFailed: props<{ error: HttpErrorResponse }>(),
    formFetchedWithFiles: props<{ form: StoreCollectionForm, addToViewStack: boolean, files: Array<File> }>(),
    formFetchedWithProtectedField: props<{
      form: StoreCollectionForm,
      addToViewStack: boolean,
      formFields: Array<{ protectedFieldType: ProtectedFieldType, value: any }>
    }>(),
    formFetchedWithTemplate: props<{
      form: StoreCollectionForm, addToViewStack: boolean, selectedTemplate: {
        mediaId: number,
        sourceCollectionId: number,
        instanceId: number,
        versionId: number,
        targetCollection: number
      }
    }>(),
    formFetchedByWidgetIdWithCategory: props<{
      form: StoreCollectionForm,
      addToViewStack: boolean,
      lookupItem: LookupItem,
      skillCategoryCollectionId: number
    }>(),

    // Closing Form
    formCanceled: props<{
      formId: string,
      form: CollectionForm,
      unsavedChanges: boolean,
      performFileCheckoutValidation: boolean,
      ignoreReroute?: boolean
    }>(),
    performGoBack: emptyProps(),
    performGoBackFailed: emptyProps(),
    formCanceledFileCheckoutDialogValidation: props<{
      formId: string,
      form: CollectionForm,
      unsavedChanges: boolean
    }>(),
    formClosed: props<{ formId: string, unsavedChanges: boolean }>(),
    checkForUnsavedChanges: props<{ formId: string, form: CollectionForm }>(),

    // Form Field actions
    updateFileFields: props<{ formId: string, field: CollectionFormField, file: MediaCommitDto | null }>(),
    clearFileFields: props<{ formId: string, field: CollectionFormField }>(),
    updateIntegrity: props<{ formId: string, integrity: Map<number, SkillIntegrityCheck> | undefined }>(),
    updateScheduler: props<{ formId: string, scheduler: SchedulerDto }>(),

    updateFormCheckedOutBy: props<{ formId: string, checkOutByUserId: number }>(),
    updateFormPermissions: props<{ formId: string, permissions: InstancePermissionsDto }>(),
    updateFormCheckedIn: props<{
      formId: string,
      file: { title: string, id: number, extension: string, size: number }
    }>(),
    updateFormField: props<{ formId: string, fieldId: number, value: unknown }>(),
    updateFormFieldFailed: emptyProps(),
    updateGridFormField: props<{
      formId: string,
      gridFieldId: number,
      recordId: number,
      recordFieldId: number,
      value: unknown
    }>(),
    updateFormFieldValue: props<{ formId: string, fieldId: number, value: unknown }>(),
    updateFormFieldSubscribedUsers: props<{
      formId: string,
      fieldId: number,
      orgChart: Array<OrgChartItem>,
      users: Array<TrnFieldProperties>,
      deletedUsers: Array<TrnFieldProperties>
    }>(),
    createChecklistGroupValue: props<{
      formId: string,
      gridFieldId: number,
      recordId: number | undefined,
      collectionFieldsId: number,
      caption: string
    }>(),
    createComboboxValueSucceeded: props<{
      formId: string,
      gridFieldId: number,
      recordId: number | undefined,
      fieldId: number,
      options: Array<CollectionFormFieldValue>,
      value: number | undefined
    }>(),
    mapComboboxValues: props<{
      formId: string,
      gridFieldId: number,
      recordId: number | undefined,
      fieldId: number,
      options: Array<CollectionFormFieldValue>,
      value: number | undefined
    }>(),
    updateFormFieldCaption: props<{ formId: string, fieldId: number, value: unknown }>(),
    updateFormFieldAiGeneration: props<{
      formId: string,
      fieldId: number,
      gridFieldId: number | undefined,
      recordId: number | undefined,
      value: { aiValue: any, generated: boolean, confirmed: boolean | undefined, aiButtonCollectionFieldId?: number | undefined} | undefined,
      isFirstTimeValidation?: boolean | undefined
    }>(),
    updateFormAllFieldAiGeneration: props<{ formId: string, fieldId: number }>(),
    updateAiLoading: props<{ formId: string, fieldId: number, aiLoading: boolean }>(),
    updateAiLoadingInGrid: props<{
      formId: string,
      fieldId: number,
      gridFieldId: number,
      recordId: number,
      aiLoading: boolean
    }>(),
    checkFormFieldAiGeneration: props<{ formId: string }>(),
    setAiUnsavedChanges: props<{ formId: string; unsavedChanges: boolean }>(),
    updateGridFormFieldValue: props<{
      formId: string,
      gridFieldId: number,
      recordId: number,
      recordFieldId: number,
      value: unknown
    }>(),
    updateGridFormFieldRecords: props<{
      formId: string,
      gridFieldId: number,
      records: Record[],
    }>(),
    updateFormFieldEnumValue: props<{ formId: string, fieldId: number, value: number }>(),
    // TODO: add reducer function for action
    updateGridFormFieldEnumValue: props<{
      formId: string,
      gridFieldId: number,
      recordId: number,
      recordFieldId: number,
      value: number
    }>(),
    updateOrgChartFieldValue: props<{
      formId: string,
      fieldId: number,
      value: Array<OrgChartItem>
    }>(),
    updateRegisteredOnBehalfOfFieldValueWithEntity: props<{
      formId: string,
      fieldId: number,
      entityField: CollectionFormField,
      value: Array<OrgChartItem>
    }>(),
    updateGridOrgChartFieldValue: props<{
      formId: string,
      gridFieldId: number,
      recordId: number,
      recordFieldId: number,
      value: Array<OrgChartItem>
    }>(),
    setFieldReadOnly: props<{
      formId: string,
      formFieldId: number,
      isReadOnly: boolean,
      readOnlyPriority: ReadOnlyPriority
    }>(),
    setFieldLoading: props<{
      formId: string,
      formFieldId: number,
      isLoading: boolean
    }>(),
    setFieldsLoading: props<{
      formId: string,
      formFieldIds: number[],
      isLoading: boolean
    }>(),
    setFieldProperty: props<{
      formId: string,
      fieldId: number,
      property: keyof CollectionFormField,
      value: unknown
    }>(),
    setGridFieldReadOnly: props<{
      formId: string,
      fieldId: number,
      recordId: number,
      recordFieldId: number,
      isReadOnly: boolean,
      readOnlyPriority: ReadOnlyPriority
    }>(),

    // Grid: Add Empty Editable Row
    addEmptyRowToGrid: props<{ formId: string, gridFieldId: number }>(),
    getEmptyRowForGridSucceeded: props<{
      formId: string,
      gridFieldId: number,
      emptyRow: Record
    }>(),
    getEmptyRowForGridFailed: props<{ formId: string, error: Error }>(),

    addInstancesToGrid: props<{
      formId: string,
      gridFieldId: number,
      instances: CollectionListDataInstance[],
      aiGenerated?: boolean
    }>(),

    confirmAddInstancesToGrid: props<{
      formId: string,
      gridFieldId: number,
      instances: CollectionListDataInstance[]
    }>(),
    invokeFormSave: props<{ formId: string, closeStep: boolean, closeForm: boolean }>(),
    addViewDataInstanceToGrid: props<{
      formId: string,
      instanceId: number,
      versionId: number,
      viewDataSourceId: number,
      linkedCollectionType: LinkedCollectionType,
      originalChildInstancesId: number,
      crossLinkInstancesId?: number,
      viewDataSources: Array<ViewDataSource>,
      targetGridField: CollectionFormField,
      row: Record | undefined,
      isNewInstanceOnLookup: boolean
    }>(),
    addViewDataInstancesToGrid: props<{
      formId: string,
      request: Array<{
        instanceId: number,
        versionId: number,
        originalChildInstancesId: number,
        crossLinkInstancesId: number,
        parentInstanceId?: number
      }>,
      viewDataSourceId: number,
      viewDataSources: Array<ViewDataSource>,
      targetGridField: CollectionFormField,
      row: Record | undefined,
      isNewInstanceOnLookup: boolean
    }>(),
    addRowToGrid: props<{ formId: string, gridFieldId: number, updatedForm: CollectionForm }>(),
    rowAddedToGrid: props<{ formId: string, gridFieldId: number }>(),

    // Add Grid Record(s)
    addRowsToGrid: props<{ formId: string, gridFieldId: number, records: Record[] }>(),
    rowsAddedToGrid: props<{ formId: string, gridFieldId: number }>(),

    // Remove Grid Record(s)
    removeRecordFromGrid: props<{ formId: string, gridFieldId: number, recordId: number }>(),
    removeRecordsFromGrid: props<{ formId: string, gridFieldId: number, recordIds: Array<number> }>(),
    removeRecordFromGridPreConfirmation: props<{ formId: string, gridFieldId: number, recordId: number }>(),
    removeRecordsFromGridPreConfirmation: props<{ formId: string, gridFieldId: number, recordIds: Array<number> }>(),
    recordRemovedFromGrid: props<{ formId: string, gridFieldId: number }>(),
    clearGrid: props<{ formId: string, gridFieldId: number }>(),

    // Updating Grid Records
    updateGridRecord: props<{
      formId: string,
      viewDataSourceId: number,
      lookupData: CollectionFormFieldGridLookupData,
      recordId: number,
      newRecordId?: number
    }>(),
    gridRecordUpdated: props<{ formId: string, viewDataSourceId: number, recordId: number }>(),
    updateGridRecordWithLookup: props<{
      formId: string,
      gridFieldId: number,
      recordId: number,
      lookupData: CollectionFormFieldGridLookupData,
      lookupItem: LookupItem
    }>(),
    updateGridRecordInstance: props<{ formId: string, gridFieldId: number, recordId: number, instanceId: number }>(),
    updateGridRecordEditMode: props<{
      formId: string,
      gridFieldId: number,
      recordId: number,
      editMode: boolean
    }>(),
    updateGridRecordEditModeWithPreValidation: props<{
      formId: string,
      gridFieldId: number,
      recordId: number,
      editMode: boolean
    }>(),
    invalidGridRecordEditModeAction: emptyProps(),
    // Scripting & Formula's
    performOnOpenScripting: props<{ formId: string }>(),
    scriptingTriggered: props<{ formId: string, fieldId: number }>(),
    scriptingEvaluating: props<{ formId: string, onOpen: boolean }>(),
    scriptingExecutingActions: props<{ formId: string, actions: EvaluationActionDto[] }>(),
    scriptingComplete: props<{ formId: string }>(),
    scriptingFailed: props<{ formId: string }>(),
    updateScriptingForm: props<{ update: Update<StoreCollectionForm> }>(),

    // Formula's
    calculateFormulaField: props<{ formId: string, fieldId: number }>(),
    calculateGridFormulaField: props<{ formId: string, gridFieldId: number, recordId: number, fieldId: number }>(),
    calculateGridFormula: props<{ formId: string, gridFieldId: number }>(),
    calculateFormulas: props<{ formId: string }>(),
    calculateSetFormulas: props<{ formId: string, formulas: Set<CollectionFormFormulaDto> }>(),
    updateFormulaOutputField: props<{ formId: string, fieldId: number, value: unknown }>(),
    updateFormulaOutputGridField: props<{
      formId: string,
      gridFieldId: number,
      recordId: number,
      fieldId: number,
      value: unknown
    }>(),

    // Locks
    deleteLock: props<{ formId: string, form: CollectionForm }>(),
    deleteExceededLock: props<{ formId: string, form: CollectionForm }>(),
    lockDeleted: props<{ formId: string, form: CollectionForm }>(),
    deleteSelfLock: props<{ formId: string, form: CollectionForm, close: boolean }>(),
    selfLockDeleted: props<{ formId: string, form: CollectionForm, close: boolean }>(),

    // Change Reason
    requestChangeReason: props<{ formId: string, existingReason: string | undefined }>(),
    changeReasonSubmitted: props<{ formId: string, changeReason: string }>(),

    // Signature
    requestSignature: props<{ formId: string }>(),
    signatureSubmitted: props<{ formId: string, signature: unknown }>(),

    // Single Record Lookups
    /**
     * Props factory used instead of standard props<> due to need for optional parameter.
     */
    lookupChanged: (props: {
      formId: string,
      lookupFieldId: number,
      lookupItem: LookupItem,
      clearChildren?: boolean,
      showFormLoader?: boolean
    }) => ({
      formId: props.formId,
      lookupFieldId: props.lookupFieldId,
      lookupItem: props.lookupItem,
      clearChildren: props.clearChildren === undefined ? true : props.clearChildren,
      showFormLoader: props.showFormLoader === undefined ? true : props.showFormLoader
    }),

    lookupCreated: props<{
      formId: string,
      lookupFieldId: number,
      lookupItem: LookupItem
    }>(),
    lookupCleared: props<{
      formId: string,
      viewDataSourceId: number
    }>(),
    lookupFetched: props<{
      formId: string,
      lookupFieldId: number,
      lookupItem: LookupItem,
      lookupData: CollectionFormFieldSingleLookupData | undefined
    }>(),
    lookupFailed: props<{
      formId: string
    }>(),
    templateNameLookupChanged: (props: {
      formId: string,
      form: StoreCollectionForm,
      lookupField: CollectionFormField,
      lookupItem: LookupItem,
      clearChildren?: boolean
    }) => ({
      formId: props.formId,
      form: props.form,
      lookupField: props.lookupField,
      lookupItem: props.lookupItem,
      clearChildren: props.clearChildren === undefined ? true : props.clearChildren
    }),
    // Grid Record lookups
    /**
     * Fills a lookup field (in grid)
     */
    gridLookupChanged: props<{
      formId: string,
      gridFieldId: number,
      recordId: number,
      lookupFieldId: number,
      lookupItem: LookupItem,
      ignoreCrossLinkFields?: boolean
    }>(),
    gridLookupCreated: props<{
      formId: string,
      gridFieldId: number,
      recordId: number,
      lookupFieldId: number,
      lookupItem: LookupItem
    }>(),
    /**
     * Empties a lookup field (in grid)
     */
    gridLookupCleared: props<{
      formId: string,
      gridFieldId: number,
      recordId: number,
      viewDataSourceId: number
    }>(),
    gridRecordDataLookupUpdated: props<{
      formId: string,
      recordId: number,
      lookupFieldId: number,
      gridFieldId: number,
      lookupData: CollectionFormFieldGridLookupData
    }>(),
    gridLookupFetched: props<{
      formId: string,
      gridFieldId: number,
      recordId: number,
      lookupFieldId: number,
      lookupItem: LookupItem,
      lookupData: CollectionFormFieldGridLookupData | undefined
    }>(),
    gridLookupFailed: props<{
      formId: string
    }>(),
    // Older more complex lookups
    // TODO: refactor
    fetchGridLookupNewRecord: props<{
      formId: string,
      gridFieldId: number,
      summaryItem: CollectionListSummaryItem
    }>(),
    fetchGridLookupNewRecords: props<{
      formId: string,
      gridFieldId: number,
      summaryItems: Array<CollectionListSummaryItem>
    }>(),
    fetchLookupByCrossLinkId: props<{
      formId: string,
      storeForm: StoreCollectionForm,
      gridParentFilter: GridParentFilter,
    }>(),
    updateFormViewDataSource: props<{
      formId: string,
      viewDataSource: ViewDataSource,
      ignoreReadonlySetters?: boolean,
      aiGenerated?: boolean
    }>(),
    updateFormViewDataSources: props<{ formId: string, viewDataSources: Array<ViewDataSource> }>(),
    enableDisableLinkedFields: props<{
      formId: string,
      form: CollectionForm,
      field: CollectionFormField,
      value: CollectionFormFieldGridLookupData | CollectionFormFieldSingleLookupData
    }>(),
    updateGridLinkedFieldValues: props<{
      formId: string,
      form: CollectionForm,
      fieldId: number,
      recordId: number,
      field: CollectionFormField,
      summaryItem: CollectionListSummaryItem,
      value: CollectionFormFieldGridLookupData
    }>(),
    clearLinkedFieldValues: props<{ formId: string, field: CollectionFormField }>(),
    clearGridLinkedFieldValues: props<{
      formId: string,
      fieldId: number,
      recordId: number,
      field: CollectionFormField
    }>(),

    // Form Validation
    validateSchedulerForm: props<{
      formId: string,
      form: CollectionForm,
      closeForm: boolean,
      schedule: SchedulerDto
    }>(),
    preValidateInstance: props<{
      formId: string,
      form: CollectionForm,
      closeStep: boolean,
      closeForm: boolean
    }>(),
    preValidateInstanceOnBehalfOf: props<{
      formId: string,
      form: CollectionForm,
      closeStep: boolean,
      closeForm: boolean
    }>(),
    preValidationFailed: props<{ formId: string, response: HttpErrorResponse }>(),
    formValidationSucceeded: emptyProps(),
    formValidationFailed: props<{ formId: string, validationErrors: CollectionFormValidatorResult }>(),
    clearFormValidation: props<{ formId: string }>(),
    clearFormFieldValidation: props<{ formId: string, fieldId: number }>(),

    // AI Button
    getAiButtonData: props<{
      formId: string,
      field: CollectionFormField,
      linkedFields: CollectionFormField[] | undefined
    }>(),
    getAiButtonDataInGrid: props<{
      formId: string,
      fieldId: number,
      field: CollectionFormField,
      gridFieldId: number,
      recordId: number,
      linkedFields: CollectionFormField[] | undefined
    }>(),
    fetchAiButtonData: props<{
      formId: string,
      field: CollectionFormField,
      data: AiButtonModel,
      gridFieldId: number | undefined,
      recordId: number | undefined
    }>(),
    fetchAiButtonDataSucceeded: props<{
      formId: string,
      field: CollectionFormField,
      data: AiButtonResponse,
      gridFieldId: number | undefined,
      recordId: number | undefined,
    }>(),
    applyAiToFormField: props<{ formId: string, field: CollectionFormField, value: any }>(),
    fetchAiButtonDataFailed: props<{ formId: string, field: CollectionFormField, error: HttpErrorResponse }>(),
    updateFieldWithAi: props<{ formId: string, fieldId: number, value: AiButtonResultValue, isFirstTimeValidation?: boolean | undefined }>(),
    acceptAllAiGeneration: props<{ formId: string }>(),
    checkAiConfirmation: props<{ formId: string }>(),

    // Entity
    entityUpdateFailed: props<{ formId: string, response: HttpErrorResponse }>(),
    entityFieldNotFound: props<{ formId: string }>(),

    processChecklistPendingRequests: emptyProps(),
  }
});